import { validateProducts, validateFilters, validateSearchMeta, validateSortOptions } from '../utils/validation';
// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------
export function mapProducts(results) {
    const mapped = results.map((item) => {
        var _a;
        const price = toNumber(item.price);
        const msrp = toNumber(item.msrp);
        return {
            id: item.uid,
            title: item.name,
            handle: item.handle || ((_a = item.url) === null || _a === void 0 ? void 0 : _a.replace(/^\/products\//, '').replace(/\/$/, '')) || item.uid,
            brand: item.brand || item.vendor || '',
            color: '',
            size: [''],
            price: {
                regular: msrp > price ? msrp : price,
                sale: msrp > price ? price : undefined,
            },
            image: {
                url: item.imageUrl || item.thumbnailImageUrl || '',
                alt: item.name,
            },
            images: parseImages(item.images, item.name),
            availability: resolveAvailability(item),
            sku: item.sku,
            vendor: item.vendor || item.brand,
            productType: item.product_type,
            description: item.description,
            tags: normalizeTags(item.tags),
            rating: item.rating != null && item.ratingCount != null
                ? { average: toNumber(item.rating), count: toNumber(item.ratingCount) }
                : undefined,
            isPinned: false,
            isBoosted: false,
            variants: parseVariants(item.variants),
            customAttributes: item.ss_available != null && String(item.ss_available).length > 0
                ? { ss_available: String(item.ss_available) }
                : undefined,
        };
    });
    return validateProducts(mapped, 'Searchspring');
}
// ---------------------------------------------------------------------------
// Filters / Facets
// ---------------------------------------------------------------------------
export function mapFilters(facets, currentFilters) {
    if (!(facets === null || facets === void 0 ? void 0 : facets.length))
        return [];
    const filters = facets.map((facet) => {
        // Slider / range facets
        if (facet.type === 'slider' && facet.range) {
            const [min, max] = facet.range;
            const [activeMin, activeMax] = facet.active || facet.range;
            return {
                id: facet.field,
                label: facet.label,
                type: 'range',
                min,
                max,
                currentMin: activeMin,
                currentMax: activeMax,
                gap: facet.step,
                unit: facet.field.toLowerCase().includes('price') ? '$' : undefined,
            };
        }
        // List / grid / palette / hierarchy facets → select
        const options = (facet.values || []).map((v) => {
            var _a, _b, _c, _d;
            // Range-type values (e.g. price) use low/high instead of value
            const val = v.type === 'range'
                ? `${(_a = v.low) !== null && _a !== void 0 ? _a : '*'}-${(_b = v.high) !== null && _b !== void 0 ? _b : '*'}`
                : String((_c = v.value) !== null && _c !== void 0 ? _c : '');
            const label = String(v.label || val);
            return {
                id: val,
                label,
                count: v.count,
                selected: v.active || ((_d = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters[facet.field]) === null || _d === void 0 ? void 0 : _d.includes(val)) || false,
                value: val,
            };
        });
        return {
            id: facet.field,
            label: facet.label,
            type: facet.type === 'palette' ? 'swatch' : 'select',
            options,
            multiSelect: facet.multiple !== 'single',
            isCollapsed: facet.collapse === 1,
        };
    });
    return validateFilters(filters, 'Searchspring');
}
// ---------------------------------------------------------------------------
// Meta / Pagination
// ---------------------------------------------------------------------------
export function mapMeta(data) {
    var _a;
    const { pagination, merchandising, didYouMean, query } = data;
    const meta = {
        totalResults: pagination.totalResults,
        totalPages: pagination.totalPages,
        currentPage: pagination.currentPage,
        pageSize: pagination.perPage,
        hasNextPage: pagination.currentPage < pagination.totalPages,
        hasPreviousPage: pagination.currentPage > 1,
    };
    // Spell correction
    if ((query === null || query === void 0 ? void 0 : query.corrected) || didYouMean) {
        meta.spellCheck = {
            original: (query === null || query === void 0 ? void 0 : query.original) || '',
            corrected: (query === null || query === void 0 ? void 0 : query.corrected) || (didYouMean === null || didYouMean === void 0 ? void 0 : didYouMean.query) || '',
            suggestions: didYouMean ? [didYouMean.query] : [],
        };
    }
    // Merchandising info
    if (merchandising) {
        meta.merchandisingApplied = {
            pinnedProducts: merchandising.elevated,
            hiddenProducts: merchandising.removed,
            rules: (_a = merchandising.triggeredCampaigns) === null || _a === void 0 ? void 0 : _a.map(c => ({
                id: c.id,
                name: c.title,
                type: c.type,
            })),
        };
    }
    return validateSearchMeta(meta, 'Searchspring');
}
// ---------------------------------------------------------------------------
// Sort options
// ---------------------------------------------------------------------------
export function mapSorts(data) {
    var _a, _b;
    if ((_b = (_a = data.sorting) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.length) {
        // Always include Default as the first option
        const options = [{ id: 'relevance', label: 'Default' }];
        for (const opt of data.sorting.options) {
            if (opt.field === 'relevance')
                continue; // Already added above
            const id = `${opt.field}:${opt.direction}`;
            if (options.some(o => o.id === id))
                continue;
            options.push({ id, label: opt.label });
        }
        return validateSortOptions(options, 'Searchspring');
    }
    // Fallback defaults
    return validateSortOptions([
        { id: 'relevance', label: 'Default' },
        { id: 'ss_price:asc', label: 'Price: Low to High' },
        { id: 'ss_price:desc', label: 'Price: High to Low' },
        { id: 'title:asc', label: 'Name: A - Z' },
        { id: 'title:desc', label: 'Name: Z - A' },
    ], 'Searchspring');
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function parseVariants(raw) {
    if (!raw)
        return [];
    let parsed;
    if (typeof raw === 'string') {
        try {
            const decoded = raw.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&#39;/g, "'");
            parsed = JSON.parse(decoded);
        }
        catch (_a) {
            return [];
        }
    }
    else if (Array.isArray(raw)) {
        parsed = raw;
    }
    else {
        return [];
    }
    return parsed.map((v) => {
        var _a, _b;
        const variantPrice = toNumber(v.price);
        const compareAt = toNumber(v.compare_at_price);
        return {
            id: String(v.id),
            title: v.title || '',
            sku: v.sku || undefined,
            price: {
                regular: compareAt > variantPrice ? compareAt : variantPrice,
                sale: compareAt > variantPrice ? variantPrice : undefined,
            },
            available: (_a = v.available_for_sale) !== null && _a !== void 0 ? _a : true,
            inventoryQuantity: v.inventory_quantity,
            selectedOptions: v.selected_options || [],
            image: ((_b = v.featured_image) === null || _b === void 0 ? void 0 : _b.src) ? { url: v.featured_image.src, alt: v.title || '' } : undefined,
        };
    });
}
function parseImages(raw, alt) {
    if (!raw)
        return undefined;
    const urls = Array.isArray(raw) ? raw : String(raw).split('|');
    const result = urls.map(u => u.trim()).filter(Boolean).map(url => ({ url, alt }));
    return result.length ? result : undefined;
}
function toNumber(val) {
    if (val == null)
        return 0;
    const n = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(n) ? 0 : n;
}
function normalizeTags(raw) {
    if (!raw)
        return undefined;
    if (Array.isArray(raw)) {
        const tags = raw.map(t => String(t).trim()).filter(Boolean);
        return tags.length ? tags : undefined;
    }
    // Searchspring may return tags as a pipe- or comma-separated string
    const tags = String(raw).split(/[|,]/).map(t => t.trim()).filter(Boolean);
    return tags.length ? tags : undefined;
}
function resolveAvailability(item) {
    if (item.ss_available != null) {
        return item.ss_available === 'true' || item.ss_available === '1' ? 'in_stock' : 'out_of_stock';
    }
    if (item.stockMessage) {
        return item.stockMessage.toLowerCase().includes('out') ? 'out_of_stock' : 'in_stock';
    }
    return 'in_stock';
}
